import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { motion } from "framer-motion";
import {
  Building2, TrendingUp, Shield, Globe2, Heart,
  CheckCircle, LayoutGrid, PhilippinePeso, Award, Users,
  Megaphone, ArrowRight, ChevronRight, MapPin, Dumbbell,
  CalendarCheck, Star, Phone
} from "lucide-react";

const fadeUp = { hidden: { opacity: 0, y: 30 }, visible: { opacity: 1, y: 0 } };
const stagger = { visible: { transition: { staggerChildren: 0.1 } } };

export default function ForVenues() {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-white overflow-hidden">
      {/* ── Navigation ── */}
      <nav className="fixed top-0 w-full z-50 bg-white/95 backdrop-blur border-b border-slate-100">
        <div className="max-w-7xl mx-auto flex items-center justify-between px-4 sm:px-8 h-16">
          <div className="flex items-center gap-2 cursor-pointer" onClick={() => navigate("/")}>
            <img src="/logo.png" alt="SetPlay" className="h-9 w-9" />
            <span className="font-heading text-xl font-bold uppercase tracking-tight text-court-green">SetPlay</span>
          </div>
          <div className="hidden md:flex items-center gap-6 text-sm font-medium text-slate-600">
            <a href="#why" className="hover:text-court-green transition-colors">Why SetPlay</a>
            <a href="#features" className="hover:text-court-green transition-colors">Features</a>
            <a href="#welcome" className="hover:text-court-green transition-colors">Venue Types</a>
            <button onClick={() => navigate("/")} className="hover:text-court-green transition-colors">Back to Home</button>
          </div>
          <div className="flex items-center gap-3">
            <Button variant="ghost" onClick={() => navigate("/login")} className="text-sm font-medium" data-testid="nav-login">Login</Button>
            <Button onClick={() => navigate("/list-venue")} className="bg-court-orange hover:bg-court-orange-hover text-white rounded-full px-6 text-sm font-heading uppercase" data-testid="nav-list-venue">List Your Venue</Button>
          </div>
        </div>
      </nav>

      {/* ═══════════════════════════════════════════════
          HERO
          ═══════════════════════════════════════════════ */}
      <section className="pt-16 relative min-h-[92vh] flex items-center" data-testid="venues-hero-section">
        <div className="absolute inset-0">
          <img
            src="https://images.unsplash.com/photo-1559369064-c4d65141e408?w=1920&q=80"
            alt="Indoor basketball court"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-court-green/92 via-court-green/85 to-court-green/95" />
        </div>

        <div className="relative z-10 max-w-5xl mx-auto px-4 sm:px-8 py-24 text-center">
          <motion.div initial="hidden" animate="visible" variants={stagger}>
            <motion.p variants={fadeUp} className="text-court-orange font-heading uppercase tracking-[0.35em] text-sm mb-5">
              Courts · Gyms · Studios · Sports Centers · Community Facilities
            </motion.p>
            <motion.h1 variants={fadeUp} className="font-heading uppercase tracking-tighter font-bold text-white text-5xl md:text-7xl lg:text-8xl mb-6" data-testid="hero-heading">
              For Venues
            </motion.h1>
            <motion.p variants={fadeUp} className="text-white/80 text-lg md:text-xl max-w-2xl mx-auto mb-4 leading-relaxed">
              Private man o LGU-managed, tinutulungan ng SetPlay ang sports at fitness venues na maging mas organized, mas profitable, at mas discoverable.
            </motion.p>
            <motion.p variants={fadeUp} className="text-white/60 text-base max-w-xl mx-auto mb-10 leading-relaxed">
              Gawing mas smart, mas profitable, at mas trusted ang sports o fitness venue mo.
            </motion.p>
            <motion.div variants={fadeUp} className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button onClick={() => navigate("/list-venue")} size="lg"
                className="bg-court-orange hover:bg-court-orange-hover text-white rounded-full px-10 h-14 text-base font-heading uppercase tracking-wide shadow-lg" data-testid="cta-list-venue">
                <Building2 className="w-5 h-5 mr-2" />List Your Venue
              </Button>
              <Button onClick={() => navigate("/login?tab=register")} variant="outline" size="lg"
                className="bg-white/10 backdrop-blur border-white/30 text-white hover:bg-white/20 rounded-full px-10 h-14 text-base font-heading uppercase tracking-wide" data-testid="cta-talk-sales">
                <Phone className="w-5 h-5 mr-2" />Talk to SetPlay
              </Button>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* ═══════════════════════════════════════════════
          WHY SETPLAY FOR VENUES?
          ═══════════════════════════════════════════════ */}
      <section id="why" className="py-20 md:py-32 bg-white" data-testid="why-setplay-section">
        <div className="max-w-6xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-12 md:gap-16 items-start">
            {/* Left: Problem */}
            <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={stagger}>
              <motion.p variants={fadeUp} className="text-court-orange font-heading uppercase tracking-[0.2em] text-sm mb-3">
                Bakit SetPlay?
              </motion.p>
              <motion.h2 variants={fadeUp} className="font-heading uppercase tracking-tight font-bold text-court-green text-3xl md:text-5xl mb-6">
                Why SetPlay<br />for Venues?
              </motion.h2>
              <motion.p variants={fadeUp} className="text-slate-700 text-base md:text-lg leading-relaxed">
                Hindi na sapat ngayon ang manual scheduling, chat-based bookings, at paiba-ibang arrangements sa venue management. Kapag magulo ang booking flow, unclear ang availability, o hindi maximized ang paggamit ng spaces, nawawalan ka ng kita at tiwala ng customers.
              </motion.p>
              <motion.p variants={fadeUp} className="text-slate-500 text-base mt-4 leading-relaxed">
                SetPlay is built for sports and fitness venue owners, operators, and facility managers who want to run their venue more professionally and grow in a more organized way.
              </motion.p>
            </motion.div>

            {/* Right: Benefits */}
            <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={stagger} className="space-y-5">
              <motion.p variants={fadeUp} className="text-sm font-medium text-slate-500 uppercase tracking-wider mb-2">
                Sa SetPlay, mas madali mong mapapaandar ang venue mo:
              </motion.p>
              {[
                { icon: LayoutGrid, text: "Ayusin at i-digitize ang spaces, courts, at schedules" },
                { icon: PhilippinePeso, text: "I-maximize ang kita sa bawat oras at bawat area" },
                { icon: Award, text: "Mag-build ng mas credible at professional na presence" },
                { icon: TrendingUp, text: "Palaguin ang bookings, repeat customers, at visibility" },
                { icon: Heart, text: "Mag-contribute sa healthier and more active na Pilipinas" },
              ].map((item, i) => (
                <motion.div key={i} variants={fadeUp} className="flex items-start gap-4 group">
                  <div className="w-10 h-10 rounded-lg bg-court-green/5 flex items-center justify-center flex-shrink-0 group-hover:bg-court-orange/10 transition-colors">
                    <item.icon className="w-5 h-5 text-court-green group-hover:text-court-orange transition-colors" strokeWidth={1.5} />
                  </div>
                  <p className="text-slate-700 text-base leading-relaxed pt-2">{item.text}</p>
                </motion.div>
              ))}
            </motion.div>
          </div>
        </div>
      </section>

      {/* ═══════════════════════════════════════════════
          FEATURE CARDS — BENTO GRID
          ═══════════════════════════════════════════════ */}
      <section id="features" className="py-20 md:py-32 bg-slate-50" data-testid="features-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={stagger} className="text-center mb-14">
            <motion.p variants={fadeUp} className="text-court-orange font-heading uppercase tracking-[0.2em] text-sm mb-3">
              Mga Feature
            </motion.p>
            <motion.h2 variants={fadeUp} className="font-heading uppercase tracking-tight font-bold text-court-green text-3xl md:text-5xl">
              What You Get with SetPlay
            </motion.h2>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-8 lg:grid-cols-12 gap-5 auto-rows-auto">
            {/* Card 1: Organize Digitally (Hero Card) */}
            <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp}
              className="col-span-full md:col-span-8 lg:col-span-8 row-span-2 bg-night-asphalt text-white rounded-lg border border-white/10 p-8 md:p-12 flex flex-col justify-between hover:-translate-y-1 transition-transform" data-testid="feature-card-organize">
              <div>
                <LayoutGrid className="w-8 h-8 text-court-orange mb-5" strokeWidth={1.5} />
                <h3 className="font-heading uppercase tracking-tight font-bold text-2xl md:text-3xl mb-2">Ayusin ang Venue Mo Digitally</h3>
                <p className="text-white/60 text-sm font-heading uppercase tracking-wider mb-4">Less gulo. More control.</p>
                <p className="text-white/75 text-base leading-relaxed mb-6 max-w-lg">
                  Hindi dapat nakakalito ang pag-manage ng courts, studios, classes, at facility schedules. Sa SetPlay, puwede mong i-setup ang venue mo in a more structured and digital way.
                </p>
              </div>
              <div className="space-y-2">
                <p className="text-xs uppercase tracking-widest text-court-orange font-heading">What you can manage:</p>
                {["Full court, half court, studio, gym floor, at multi-purpose spaces", "Schedules at bookable time slots", "Available sports and activities per space", "Capacity, access rules, at usage setup", "Facility details na mas clear for customers"].map((t, i) => (
                  <div key={i} className="flex items-center gap-2.5">
                    <CheckCircle className="w-4 h-4 text-court-orange flex-shrink-0" />
                    <span className="text-white/70 text-sm">{t}</span>
                  </div>
                ))}
              </div>
            </motion.div>

            {/* Card 2: Maximize Revenue (Tall) */}
            <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp}
              className="col-span-full md:col-span-4 lg:col-span-4 row-span-2 bg-white rounded-lg border border-court-orange/30 p-8 hover:-translate-y-1 transition-transform" data-testid="feature-card-revenue">
              <PhilippinePeso className="w-8 h-8 text-court-orange mb-5" strokeWidth={1.5} />
              <h3 className="font-heading uppercase tracking-tight font-bold text-court-green text-xl mb-2">I-maximize ang Kita sa Bawat Space</h3>
              <p className="text-slate-400 text-sm font-heading uppercase tracking-wider mb-4">Every hour matters. Every space can earn.</p>
              <p className="text-slate-600 text-sm leading-relaxed mb-6">
                Kapag mas visible at mas organized ang venue mo, mas mataas ang chance na mapuno ang available slots mo at masulit ang bawat bahagi ng facility.
              </p>
              <div className="space-y-2.5">
                {["Reduce idle o bakanteng hours", "Improve visibility of available spaces", "Support different booking types", "Create more earning opportunities", "Better use of peak and non-peak periods"].map((t, i) => (
                  <div key={i} className="flex items-center gap-2.5">
                    <CheckCircle className="w-4 h-4 text-court-orange flex-shrink-0" />
                    <span className="text-slate-600 text-sm">{t}</span>
                  </div>
                ))}
              </div>
            </motion.div>

            {/* Card 3: Professional Image */}
            <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp}
              className="col-span-full md:col-span-4 lg:col-span-4 bg-white rounded-lg border border-slate-200 p-8 hover:-translate-y-1 transition-transform" data-testid="feature-card-trust">
              <Shield className="w-8 h-8 text-court-green mb-5" strokeWidth={1.5} />
              <h3 className="font-heading uppercase tracking-tight font-bold text-court-green text-xl mb-2">Magmukhang Mas Professional</h3>
              <p className="text-slate-400 text-sm font-heading uppercase tracking-wider mb-3">Trust drives bookings.</p>
              <p className="text-slate-600 text-sm leading-relaxed">
                Mas gusto ng players, parents, teams, at fitness communities ang venue na malinaw ang details, maayos ang presentation, at easy to book.
              </p>
            </motion.div>

            {/* Card 4: Grow Reach (Tall) */}
            <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp}
              className="col-span-full md:col-span-4 lg:col-span-4 row-span-2 bg-court-green text-white rounded-lg border border-court-green p-8 hover:-translate-y-1 transition-transform" data-testid="feature-card-reach">
              <Megaphone className="w-8 h-8 text-court-orange mb-5" strokeWidth={1.5} />
              <h3 className="font-heading uppercase tracking-tight font-bold text-xl mb-2">Palaguin ang Reach ng Venue Mo</h3>
              <p className="text-white/50 text-sm font-heading uppercase tracking-wider mb-4">Mas maraming makakakita. Mas maraming mag-book.</p>
              <p className="text-white/70 text-sm leading-relaxed mb-6">
                SetPlay helps connect your venue to people actively looking for courts, classes, training spaces, games, and fitness activities.
              </p>
              <div className="space-y-2.5">
                <p className="text-xs uppercase tracking-widest text-court-orange font-heading">You can attract:</p>
                {["Casual players", "Regular sports groups", "Coaches and trainers", "Fitness communities", "Event organizers", "Families, schools, corporates"].map((t, i) => (
                  <div key={i} className="flex items-center gap-2.5">
                    <ChevronRight className="w-4 h-4 text-court-orange flex-shrink-0" />
                    <span className="text-white/70 text-sm">{t}</span>
                  </div>
                ))}
              </div>
            </motion.div>

            {/* Card 5: National Impact */}
            <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp}
              className="col-span-full md:col-span-4 lg:col-span-4 bg-white rounded-lg border border-slate-200 p-8 hover:-translate-y-1 transition-transform" data-testid="feature-card-impact">
              <Heart className="w-8 h-8 text-red-500 mb-5" strokeWidth={1.5} />
              <h3 className="font-heading uppercase tracking-tight font-bold text-court-green text-xl mb-2">Maging Bahagi ng Mas Healthy na Pilipinas</h3>
              <p className="text-slate-400 text-sm font-heading uppercase tracking-wider mb-3">Your venue creates impact.</p>
              <p className="text-slate-600 text-sm leading-relaxed">
                Kapag mas madali mahanap, ma-book, at ma-enjoy ang sports at fitness spaces, mas maraming Pilipino ang nagiging active, connected, at healthy.
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* ═══════════════════════════════════════════════
          BENEFITS
          ═══════════════════════════════════════════════ */}
      <section className="py-20 md:py-32 bg-white" data-testid="benefits-section">
        <div className="max-w-6xl mx-auto px-6 md:px-12">
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={stagger} className="text-center mb-14">
            <motion.p variants={fadeUp} className="text-court-orange font-heading uppercase tracking-[0.2em] text-sm mb-3">Benefits</motion.p>
            <motion.h2 variants={fadeUp} className="font-heading uppercase tracking-tight font-bold text-court-green text-3xl md:text-5xl">
              Bakit Magandang Nasa SetPlay ang Venue Mo
            </motion.h2>
          </motion.div>

          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={stagger}
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-10 md:gap-12">
            {[
              { icon: CalendarCheck, title: "Mas Organized na Operations", desc: "Hindi na puro manual, chat, tawag, at follow-up. Mas klaro ang schedules, spaces, at availability." },
              { icon: LayoutGrid, title: "Mas Sulit ang Paggamit ng Space", desc: "Mapapagana mo nang mas maayos ang courts, rooms, at activity areas mo." },
              { icon: PhilippinePeso, title: "Mas Malaking Revenue Potential", desc: "Every available hour becomes a better earning opportunity." },
              { icon: Globe2, title: "Mas Malakas na Online Presence", desc: "Mas madali kang makita at ma-discover ng tamang audience." },
              { icon: Shield, title: "Mas Mataas na Credibility", desc: "Mas professional tingnan, mas madaling pagkatiwalaan." },
              { icon: Heart, title: "Mas Malaking Community Impact", desc: "Habang lumalago ang venue mo, mas nakakatulong ka rin sa kalusugan at wellness ng mga Pilipino." },
            ].map((b, i) => (
              <motion.div key={i} variants={fadeUp} className="group">
                <b.icon className="w-8 h-8 text-court-orange mb-4 group-hover:scale-110 transition-transform" strokeWidth={1.5} />
                <h3 className="font-heading uppercase tracking-tight font-bold text-court-green text-lg mb-2">{b.title}</h3>
                <p className="text-slate-600 text-sm leading-relaxed">{b.desc}</p>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* ═══════════════════════════════════════════════
          WHO'S WELCOME
          ═══════════════════════════════════════════════ */}
      <section id="welcome" className="py-20 md:py-32 bg-slate-50" data-testid="whos-welcome-section">
        <div className="max-w-6xl mx-auto px-6 md:px-12">
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={stagger} className="text-center mb-14">
            <motion.p variants={fadeUp} className="text-court-orange font-heading uppercase tracking-[0.2em] text-sm mb-3">
              Open para sa lahat
            </motion.p>
            <motion.h2 variants={fadeUp} className="font-heading uppercase tracking-tight font-bold text-court-green text-3xl md:text-5xl mb-4">
              Who's Welcome on SetPlay?
            </motion.h2>
            <motion.p variants={fadeUp} className="text-slate-600 text-base md:text-lg max-w-2xl mx-auto leading-relaxed">
              Whether privately owned or managed by LGUs, welcome sa SetPlay ang mga venue na gustong maging mas organized, mas visible, at mas ready for growth.
            </motion.p>
          </motion.div>

          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={stagger}
            className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              {
                img: "https://images.unsplash.com/photo-1768554630751-6448593749eb?w=600&q=80",
                title: "Privately Owned Facilities",
                subtitle: "Perfect for business owners and operators who want to digitize and grow their venue.",
                items: ["Basketball & badminton courts", "Gyms, dojos, dance studios", "Yoga & Pilates studios", "Indoor sports centers", "Swimming & specialty sports"],
              },
              {
                img: "https://images.unsplash.com/photo-1621271667301-cf812c561495?w=600&q=80",
                title: "LGU & Community Facilities",
                subtitle: "Welcome rin ang public sports spaces na minamanage ng cities, municipalities, at barangays.",
                items: ["Barangay covered courts", "Community gyms", "Municipal sports centers", "City-managed courts", "Public activity halls"],
              },
              {
                img: "https://images.unsplash.com/photo-1761971975769-97e598bf526b?w=600&q=80",
                title: "Mixed-Use & Multi-Purpose",
                subtitle: "Kung ang space mo ay ginagamit for different sports, fitness classes, o community programs, puwede rin.",
                items: ["Multi-sport covered courts", "Fitness + yoga studios", "Event halls for leagues", "Bookable zones & half-spaces", "Activity-based facilities"],
              },
            ].map((vt, i) => (
              <motion.div key={i} variants={fadeUp} className="bg-white rounded-lg border border-slate-200 overflow-hidden group hover:-translate-y-1 transition-transform" data-testid={`venue-type-${i}`}>
                <div className="h-52 overflow-hidden">
                  <img src={vt.img} alt={vt.title} className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" />
                </div>
                <div className="p-6 md:p-8">
                  <h3 className="font-heading uppercase tracking-tight font-bold text-court-green text-lg mb-2">{vt.title}</h3>
                  <p className="text-slate-500 text-sm mb-5 leading-relaxed">{vt.subtitle}</p>
                  <div className="space-y-2">
                    {vt.items.map((item, j) => (
                      <div key={j} className="flex items-center gap-2">
                        <CheckCircle className="w-3.5 h-3.5 text-court-orange flex-shrink-0" />
                        <span className="text-slate-600 text-sm">{item}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* ═══════════════════════════════════════════════
          WHY THIS MATTERS
          ═══════════════════════════════════════════════ */}
      <section className="py-20 md:py-32 bg-night-asphalt" data-testid="why-matters-section">
        <div className="max-w-4xl mx-auto px-6 md:px-12 text-center">
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={stagger}>
            <motion.p variants={fadeUp} className="text-court-orange font-heading uppercase tracking-[0.2em] text-sm mb-4">
              Why This Matters
            </motion.p>
            <motion.h2 variants={fadeUp} className="font-heading uppercase tracking-tight font-bold text-white text-3xl md:text-5xl mb-6 leading-tight">
              Mas maraming venue ang connected, mas maraming Pilipino ang gagalaw.
            </motion.h2>
            <motion.p variants={fadeUp} className="text-white/65 text-base md:text-lg leading-relaxed max-w-2xl mx-auto">
              Kapag mas maraming sports at fitness facilities ang digitally organized at mas madaling ma-access, mas maraming tao ang magkakaroon ng chance to play, train, move, recover, and stay healthy. SetPlay is not just helping venues grow. We're helping build a more active, connected, and healthier nation.
            </motion.p>
          </motion.div>
        </div>
      </section>

      {/* ═══════════════════════════════════════════════
          CTA BLOCK 1
          ═══════════════════════════════════════════════ */}
      <section className="py-20 md:py-28 bg-white" data-testid="cta-block-1">
        <div className="max-w-4xl mx-auto px-6 md:px-12">
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp}
            className="bg-court-green text-white rounded-[32px] p-10 md:p-16 text-center shadow-2xl">
            <h2 className="font-heading uppercase tracking-tight font-bold text-3xl md:text-4xl mb-4">
              May venue ka? I-level up natin.
            </h2>
            <p className="text-white/70 text-base md:text-lg mb-8 max-w-xl mx-auto leading-relaxed">
              Kung ikaw ay venue owner, facility manager, operator, o LGU handling sports and fitness spaces, puwedeng-puwede ka sa SetPlay.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button onClick={() => navigate("/list-venue")} size="lg"
                className="bg-court-orange hover:bg-court-orange-hover text-white rounded-full px-10 h-13 text-base font-heading uppercase tracking-wide" data-testid="cta-bottom-list-venue">
                <Building2 className="w-5 h-5 mr-2" />List Your Venue
              </Button>
              <Button onClick={() => navigate("/login?tab=register")} variant="outline" size="lg"
                className="border-white/30 text-white hover:bg-white/10 rounded-full px-10 h-13 text-base font-heading uppercase tracking-wide" data-testid="cta-bottom-get-started">
                Get Started <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
            </div>
          </motion.div>
        </div>
      </section>

      {/* ═══════════════════════════════════════════════
          CTA BLOCK 2
          ═══════════════════════════════════════════════ */}
      <section className="py-16 md:py-20 bg-slate-50" data-testid="cta-block-2">
        <div className="max-w-5xl mx-auto px-6 md:px-12 text-center">
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={stagger}>
            <motion.h2 variants={fadeUp} className="font-heading uppercase tracking-tight font-bold text-court-green text-2xl md:text-4xl mb-4">
              From manual to mas maayos.<br />From space to business growth.
            </motion.h2>
            <motion.p variants={fadeUp} className="text-slate-600 text-base md:text-lg mb-8 max-w-xl mx-auto leading-relaxed">
              Digitize your venue. Improve utilization. Build trust. Reach more players and communities with SetPlay.
            </motion.p>
            <motion.div variants={fadeUp} className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button onClick={() => navigate("/list-venue")} size="lg"
                className="bg-court-green hover:bg-court-green-hover text-white rounded-full px-10 h-13 text-base font-heading uppercase tracking-wide" data-testid="cta-join-venues">
                <Star className="w-5 h-5 mr-2" />Join SetPlay for Venues
              </Button>
              <Button onClick={() => navigate("/login?tab=register")} variant="outline" size="lg"
                className="border-court-green text-court-green hover:bg-court-green/5 rounded-full px-10 h-13 text-base font-heading uppercase tracking-wide" data-testid="cta-talk-team">
                <Phone className="w-5 h-5 mr-2" />Talk to Our Team
              </Button>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* ═══════════════════════════════════════════════
          FOOTER
          ═══════════════════════════════════════════════ */}
      <footer className="py-12 bg-[#0a2a1e] text-white/60" data-testid="footer">
        <div className="max-w-6xl mx-auto px-4 sm:px-8">
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-8 mb-10">
            <div>
              <div className="flex items-center gap-2 mb-3">
                <img src="/logo.png" alt="SetPlay" className="h-8 w-8" />
                <span className="font-heading text-lg font-bold text-white uppercase">SetPlay</span>
              </div>
              <p className="text-sm leading-relaxed">Connecting Filipino communities through sports. One game at a time.</p>
            </div>
            <div>
              <h4 className="font-heading text-sm text-white uppercase tracking-wider mb-3">Platform</h4>
              <div className="space-y-2 text-sm">
                <a href="/#how-it-works" className="block hover:text-white transition-colors">How It Works</a>
                <a href="/search" className="block hover:text-white transition-colors">Find Courts</a>
                <a href="/sessions" className="block hover:text-white transition-colors">Browse Sessions</a>
                <a href="/events" className="block hover:text-white transition-colors">Events</a>
              </div>
            </div>
            <div>
              <h4 className="font-heading text-sm text-white uppercase tracking-wider mb-3">Join Us</h4>
              <div className="space-y-2 text-sm">
                <a href="/login?tab=register" className="block hover:text-white transition-colors">For Players</a>
                <a href="/for-venues" className="block hover:text-white transition-colors font-medium text-court-orange">For Venues</a>
                <a href="/login?tab=register" className="block hover:text-white transition-colors">For Coaches</a>
                <a href="/sp/search" className="block hover:text-white transition-colors">For Sports Professionals</a>
              </div>
            </div>
            <div>
              <h4 className="font-heading text-sm text-white uppercase tracking-wider mb-3">Contact</h4>
              <div className="space-y-2 text-sm">
                <p>gamena@setplay.net</p>
                <p>Metro Manila, Philippines</p>
              </div>
            </div>
          </div>
          <div className="border-t border-white/10 pt-6 flex flex-col sm:flex-row items-center justify-between gap-3">
            <p className="text-sm">Proudly Built for the Philippines by <a href="https://sustek.ai/solutions/soctech" target="_blank" rel="noopener noreferrer" className="text-white hover:text-court-orange underline underline-offset-2 transition-colors">SusTek Labs</a></p>
            <div className="flex gap-4 text-sm">
              <a href="#" className="hover:text-white">Privacy</a>
              <a href="#" className="hover:text-white">Terms</a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
